import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Trophy } from "lucide-react";
import { EVENT_TYPES, LEVEL_OPTIONS } from "@/components/TournamentCreateForm";

export default function PBRRankings() {
  const { realUser } = useAuth();
  const navigate = useNavigate();
  const [discipline, setDiscipline] = useState("Men's Doubles");
  const [division, setDivision] = useState("");
  const [mode, setMode] = useState("52");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/pbr/rankings", { params: { discipline, division, mode } })
      .then(({ data }) => setData(data)).catch(() => setData(false));
  }, [discipline, division, mode]);

  const sel = "bg-pb-elevated border border-pb-border px-3 py-2 text-xs text-white focus:outline-none focus:border-pb-red";
  return (
    <div className="max-w-5xl mx-auto pb-16" data-testid="pbr-rankings-page">
      <div className="text-[10px] uppercase tracking-[0.35em] text-pb-red font-bold mb-1">Pickle Bullpen</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl uppercase leading-none mb-2">National Rankings</h1>
      <p className="text-xs text-zinc-500 mb-6">Positions 1–{data?.qual_size || 24} sit in the National Qualification Zone. PBR 52 counts your best {data?.best_n || 8} results in the last 52 weeks · PBR Race counts everything this season.</p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="pbr-filters">
        <select data-testid="pbr-filter-discipline" value={discipline} onChange={(e) => setDiscipline(e.target.value)} className={sel}>
          {EVENT_TYPES.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select data-testid="pbr-filter-division" value={division} onChange={(e) => setDivision(e.target.value)} className={sel}>
          <option value="">All Divisions</option>
          {LEVEL_OPTIONS.filter((l) => l !== "All").map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <div className="flex border border-pb-border">
          {[["52", "PBR 52"], ["race", "PBR Race"]].map(([v, l]) => (
            <button key={v} data-testid={`pbr-mode-${v}`} onClick={() => setMode(v)}
              className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-colors ${mode === v ? "bg-pb-red text-black" : "text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      {data === null && <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-pb-red" /></div>}
      {data === false && <div className="text-center py-16 text-zinc-500">Couldn't load rankings.</div>}
      {data && data.rows?.length === 0 && (
        <div className="border border-pb-border bg-pb-surface p-10 text-center" data-testid="pbr-empty">
          <Trophy className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <div className="font-display font-black text-lg uppercase">No ranked players yet</div>
          <p className="text-sm text-zinc-500">Rankings appear when a PBR-sanctioned {discipline} event is completed.</p>
        </div>
      )}
      {data && data.rows?.length > 0 && (
        <div className="border border-pb-border bg-pb-surface" data-testid="pbr-table">
          <div className="grid grid-cols-[3rem_1fr_4.5rem_3.5rem_3.5rem_4.5rem] gap-2 px-4 py-2 text-[9px] uppercase tracking-widest text-zinc-500 font-bold border-b border-pb-border">
            <span>Rank</span><span>Player</span><span className="text-right">Points</span><span className="text-right">Events</span><span className="text-right">Titles</span><span className="text-right">Record</span>
          </div>
          {data.rows.map((r, i) => {
            const inZone = r.rank <= data.qual_size;
            return (
              <div key={r.player_id}>
                <button data-testid={`pbr-row-${r.rank}`} onClick={() => navigate(`/road-to-nationals/${r.player_id}`)}
                  className={`w-full grid grid-cols-[3rem_1fr_4.5rem_3.5rem_3.5rem_4.5rem] gap-2 px-4 py-2.5 text-xs items-center text-left transition-colors hover:bg-white/5 ${r.player_id === realUser?.id ? "bg-pb-red/10" : ""}`}>
                  <span className={`font-display font-black text-base ${inZone ? "text-green-400" : "text-zinc-500"}`}>#{r.rank}</span>
                  <span className="text-white font-semibold truncate">{r.name}{r.player_id === realUser?.id ? " (you)" : ""}
                    {r.auto_qual && <span className="ml-2 text-[8px] font-black uppercase tracking-wider border border-green-500/50 text-green-400 px-1.5 py-0.5" data-testid={`pbr-autoqual-${r.rank}`}>{r.auto_qual}</span>}
                  </span>
                  <span className="text-right font-display font-black text-[#BEF264]">{r.points}</span>
                  <span className="text-right text-zinc-400">{r.events}</span>
                  <span className="text-right text-zinc-400">{r.championships}</span>
                  <span className="text-right text-zinc-400">{r.match_wins}–{r.match_losses}</span>
                </button>
                {r.rank === data.qual_size && (
                  <div className="flex items-center gap-2 px-4 py-1 bg-green-500/5 border-y border-green-500/30" data-testid="pbr-qual-line">
                    <span className="flex-1 border-t border-dashed border-green-500/50" />
                    <span className="text-[9px] uppercase tracking-[0.25em] text-green-400 font-bold">National Qualification Zone — Top {data.qual_size}</span>
                    <span className="flex-1 border-t border-dashed border-green-500/50" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
